/**
 * Local console logger for MS Word Add-in.
 *
 * Provides structured, readable logs for development and debugging.
 *
 * @remarks
 * Formats messages with context, timestamp, and log level.
 */

import { LOG_LEVEL } from "../constants/config";
import logtailLogger from "../services/logtain-logger-service";

const safeSerialize = (data: unknown): unknown => {
  if (data === null || data === undefined) {
    return data;
  }

  if (typeof data !== "object") {
    return data;
  }

  try {
    const seen = new WeakSet();
    const result = JSON.parse(
      JSON.stringify(data, (_, value) => {
        if (typeof value === "object" && value !== null) {
          if (seen.has(value)) {
            return "[Circular Reference]";
          }
          seen.add(value);
        }
        return value;
      })
    );
    return result;
  } catch (error) {
    return {
      __serialization_error: true,
      type: typeof data,
      toString: String(data).substring(0, 100),
    };
  }
};

export type LogLevel = "debug" | "info" | "warn" | "error" | "performance";

export interface LogEntry {
  readonly level: LogLevel;
  readonly message: string;
  readonly context?: string;
  readonly data?: unknown;
  readonly timestamp: string;
}

const LOG_LEVELS: Record<LogLevel, number> = {
  performance: 0,
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

let currentLogLevel = LOG_LEVEL as LogLevel;

export const setLogLevel = (level: LogLevel) => {
  currentLogLevel = level;
};

const shouldLog = (level: LogLevel): boolean => {
  return true;
  return LOG_LEVELS[level] >= LOG_LEVELS[currentLogLevel];
};

const formatLog = (entry: LogEntry): string => {
  const ctx = entry.context ? `[${entry.context}] ` : "";
  const lvl = entry.level.toUpperCase();
  const time = entry.timestamp;
  const msg = entry.message;
  return `[%c${lvl}%c] %c${time}%c ${ctx}${msg}`;
};

const styleLevel = (level: LogLevel): [string, string] => {
  switch (level) {
    case "info":
      return ["color: #1976d2; font-weight: bold;", ""];
    case "warn":
      return ["color: #fbc02d; font-weight: bold;", ""];
    case "error":
      return ["color: #d32f2f; font-weight: bold;", ""];
    case "debug":
      return ["color: #388e3c; font-weight: bold;", ""];
    case "performance":
      return ["color: #9c27b0; font-weight: bold;", ""];
    default:
      return ["", ""];
  }
};

export interface Logger {
  info: (message: string, context?: string, data?: unknown) => void;
  warn: (message: string, context?: string, data?: unknown) => void;
  error: (message: string, context?: string, data?: unknown) => void;
  debug: (message: string, context?: string, data?: unknown) => void;
  performance: (message: string, context?: string, data?: unknown) => void;
}

const log: Logger = {
  info: (message, context, data) => {
    const entry: LogEntry = {
      level: "info",
      message,
      context,
      data,
      timestamp: new Date().toISOString(),
    };
    const [levelStyle] = styleLevel("info");
    if (data !== undefined) {
      console.info(formatLog(entry), levelStyle, "", "color: #888;", "", data);
    } else {
      console.info(formatLog(entry), levelStyle, "", "color: #888;", "");
    }
    logtailLogger.info(entry.message, {
      context: entry.context,
      data: safeSerialize(entry.data),
      timestamp: entry.timestamp,
    });
    logtailLogger.flush();
  },
  warn: (message, context, data) => {
    const entry: LogEntry = {
      level: "warn",
      message,
      context,
      data,
      timestamp: new Date().toISOString(),
    };
    const [levelStyle] = styleLevel("warn");
    if (data !== undefined) {
      console.warn(formatLog(entry), levelStyle, "", "color: #888;", "", data);
    } else {
      console.warn(formatLog(entry), levelStyle, "", "color: #888;", "");
    }
    logtailLogger.warn(entry.message, {
      context: entry.context,
      data: safeSerialize(entry.data),
      timestamp: entry.timestamp,
    });
    logtailLogger.flush();
  },
  error: (message, context, data) => {
    const entry: LogEntry = {
      level: "error",
      message,
      context,
      data,
      timestamp: new Date().toISOString(),
    };
    const [levelStyle] = styleLevel("error");
    if (data !== undefined) {
      console.error(formatLog(entry), levelStyle, "", "color: #888;", "", data);
    } else {
      console.error(formatLog(entry), levelStyle, "", "color: #888;", "");
    }
    logtailLogger.error(entry.message, {
      context: entry.context,
      data: safeSerialize(entry.data),
      timestamp: entry.timestamp,
    });
    logtailLogger.flush();
  },
  debug: (message, context, data) => {
    const entry: LogEntry = {
      level: "debug",
      message,
      context,
      data,
      timestamp: new Date().toISOString(),
    };
    const [levelStyle] = styleLevel("debug");
    if (data !== undefined) {
      console.debug(formatLog(entry), levelStyle, "", "color: #888;", "", data);
    } else {
      console.debug(formatLog(entry), levelStyle, "", "color: #888;", "");
    }
    logtailLogger.debug(entry.message, {
      context: entry.context,
      data: safeSerialize(entry.data),
      timestamp: entry.timestamp,
    });
    logtailLogger.flush();
  },
  performance: (message, context, data) => {
    if (!shouldLog("performance")) return;
    const entry: LogEntry = {
      level: "performance",
      message,
      context,
      data,
      timestamp: new Date().toISOString(),
    };
    const [levelStyle] = styleLevel("performance");
    if (data !== undefined) {
      console.log(formatLog(entry), levelStyle, "", "color: #888;", "", data);
    } else {
      console.log(formatLog(entry), levelStyle, "", "color: #888;", "");
    }
    logtailLogger.debug(entry.message, {
      context: entry.context,
      data: safeSerialize(entry.data),
      timestamp: entry.timestamp,
    });
    logtailLogger.flush();
  },
};

export default log;
